import { getAuth } from "firebase/auth";
import { getFirestore, doc, getDoc, FieldValue } from "firebase/firestore";
import { getOrRefreshFirebaseToken } from "../../config/getFirebaseToken.js";
import addLogDocument from "../addLogDocument.js";
import { checkDB } from "../../models/DataFunctions.js";
import { getAuthCentral } from "../../config/FirebaseConfig.js";

/**
 * Updates a Firestore document via an API endpoint, mimicking Firebase's updateDoc.
 * Preserves Firebase FieldValue behavior (e.g., arrayUnion, arrayRemove) and handles
 * non-JSON-compatible types automatically.
 *
 * @param {object} docRef - Firestore document reference.
 * @param {object} data - Object containing field-value pairs to update.
 * @param {string} prefix - API endpoint prefix (e.g., "https://your-api/").
 * @returns {Promise<void>} - Resolves on success, rejects on failure (mimics Firebase SDK behavior).
 */
const updateDocApi = async (collection, id, data, prefix) => {
  if (!collection || !id || !data || typeof data !== "object") {
    console.error(
      "Invalid input: collection, id, and data must be provided",
      collection,
      id,
      data
    );
  }

  const docPath = `${collection}/${id}`;
  const token = await getOrRefreshFirebaseToken();
  const url = `${prefix}database/update-doc`;

  // Transform Firebase-specific objects (FieldValue, DocumentReference, Timestamp) to JSON-safe format
  const jsonSafeData = transformToJsonSafe(data);

  const body = {
    doc_path: docPath,
    data: jsonSafeData,
  };

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  try {
    const start = new Date();
    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(body),
    });

    const auth = getAuthCentral();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("User not logged in");
    }

    const db = getFirestore();
    const userRef = doc(db, "user", currentUser.uid);
    const userSnapshot = await getDoc(checkDB(userRef));
    let org_id = null;
    if (!userSnapshot.exists()) {
      org_id = userSnapshot.get("org_id");
    }

    const responseText = await response.text();
    const end = new Date();
    const elapsed = end - start;

    await addLogDocument(
      url,
      prefix,
      userRef,
      org_id,
      response.status.toString(),
      "",
      "",
      JSON.stringify(body),
      responseText,
      "POST",
      elapsed
    );

    if (!response.ok) {
      const errorText =
        responseText || `Failed to update document: ${response.status}`;
      throw new Error(errorText);
    } else {
      return {
        ...data,
        id: id,
        ref: doc(db, docPath), // Return the updated data with the document reference
      };
    }

    // Mimic Firebase SDK: resolve with no value on success
  } catch (error) {
    throw error; // Throw the error to mimic Firebase SDK's rejection behavior
  }
};

/**
 * Converts Firebase-specific objects to JSON-compatible formats, mimicking Firebase's behavior.
 * @param {any} input - The data to transform.
 * @returns {any} - JSON-safe version of the data, preserving Firebase FieldValue semantics.
 */
function transformToJsonSafe(input) {
  if (input === null || typeof input !== "object") {
    return input;
  }

  // Handle Firebase FieldValue (arrayUnion, arrayRemove)
  if (input instanceof FieldValue) {
    if (
      input._methodName === "arrayUnion" ||
      input._methodName === "arrayRemove"
    ) {
      const values = input._delegate?.value || input.uu || [];
      // Allow empty arrays for arrayUnion/arrayRemove to match Firebase SDK behavior
      return {
        _methodName: input._methodName,
        values: values.map(transformToJsonSafe),
      };
    }
    throw new Error(`Unsupported FieldValue type: ${input._methodName}`);
  }

  // Handle DocumentReference
  if (
    input.constructor?.name === "DocumentReference" ||
    input.type === "document"
  ) {
    return { _type: "DocumentReference", path: input.path };
  }

  if (
    input.seconds !== undefined &&
    input.nanoseconds !== undefined &&
    typeof input.seconds === "number" &&
    typeof input.nanoseconds === "number"
  ) {
    return {
      _type: "Timestamp",
      seconds: input.seconds,
      nanoseconds: input.nanoseconds,
    };
  }

  // Handle arrays
  if (Array.isArray(input)) {
    return input.map(transformToJsonSafe);
  }

  // Handle plain objects
  const result = {};
  for (const key in input) {
    if (Object.prototype.hasOwnProperty.call(input, key)) {
      result[key] = transformToJsonSafe(input[key]);
    }
  }
  return result;
}

export default updateDocApi;
